import { Component, DestroyRef, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { AuthenticationService } from 'authentication/services/authentication.service';
import { TokenService } from 'authentication/services/token.service';
import { Observable, catchError, switchMap, tap, throwError } from 'rxjs';
import { FormErrorComponent } from 'shared/components/form-error/form-error.component';
import { TimerComponent } from 'shared/components/timer/timer.component';
import { ToastService } from 'shared/services/toast.service';

@Component({
    selector: 'app-otp',
    templateUrl: './otp.component.html',
    styleUrls: ['./otp.component.scss'],
    imports: [FormErrorComponent, ReactiveFormsModule, TranslateModule, TimerComponent],
})
export class OtpComponent {
    @ViewChild('timer') timerComponent!: TimerComponent;

    @Input({ required: true }) email!: string;
    @Input() allowUserSwitching: boolean = false;

    @Output() onOtpSuccess = new EventEmitter<void>();
    @Output() onCancelOtp = new EventEmitter<void>();

    public form: FormGroup = new FormGroup({
        otp: new FormControl(null, [Validators.required, Validators.minLength(6), Validators.maxLength(6)]),
    });
    public otp: FormControl = this.form.get('otp')! as FormControl;
    public isTimerRunning = true;

    constructor(
        private authenticationService: AuthenticationService,
        private toastService: ToastService,
        private destroyRef: DestroyRef,
        private tokenService: TokenService,
    ) {}

    public disableOtp() {
        this.isTimerRunning = false;
        this.onCancelOtp.emit();
    }

    public loginWithOtp() {
        this.otp.updateValueAndValidity();
        this.otp.markAllAsTouched();

        if (this.otp.valid) {
            const otp = this.otp.value;

            this.authenticationService
                .sendOtp(otp)
                .pipe(
                    takeUntilDestroyed(this.destroyRef),
                    this.handleTokenExpirationAndRetry(() => this.authenticationService.sendOtp(otp)),
                )
                .subscribe(() => {
                    this.onOtpSuccess.emit();
                });
        }
    }

    public requestNewOtpCode() {
        this.authenticationService
            .requestNewOtpEmail()
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                this.handleTokenExpirationAndRetry(() => this.authenticationService.requestNewOtpEmail()),
            )
            .subscribe(() => {
                this.toastService.showToast({
                    body: 'generalKeywords.otp.emailResent',
                    severity: 'success',
                    duration: 30000,
                    ignoreRemoval: true,
                });
                this.isTimerRunning = true;
            });
    }

    public handleTokenExpirationAndRetry<T>(originalRequestFunction: () => Observable<T>): (source: Observable<T>) => Observable<T> {
        return (source: Observable<T>) =>
            source.pipe(
                catchError((err) => {
                    if (err.status === 456) {
                        // Token expired, get a new token and retry the original request
                        // We don't use the token interceptor since that calls a different endpoint
                        return this.authenticationService.getNewTwoFactorIdentityToken().pipe(
                            tap((newToken: string) => this.tokenService.setToken(newToken)),
                            switchMap(() => originalRequestFunction()),
                            catchError((innerErr) => throwError(() => innerErr)),
                        );
                    } else {
                        // Propagate other errors
                        return throwError(() => err);
                    }
                }),
            );
    }
}
